import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

import { IMAGE_TYPE_MENU_ITEMS } from './claimImageSelectorHelpers';

const VIEW_TYPE_TO_LABEL: Record<string, string> = {
  BITEWING: 'Bitewings',
  PERIAPICAL: 'Periapicals',
  PANORAMIC: 'Panoramic',
  fmx: 'FMX',
  periochart: 'Perio Charts',
  colorImages: 'Intraoral Photos',
};

type ImageTypeDropdownProps = {
  viewFilterImageTypes: Set<string>;
  allTypeKeys: readonly string[];
  onToggleType: (key: string) => void;
  isTypeChecked: (key: string) => boolean;
};

const MAX_TYPES_SHOWN = 2;

function getImageTypeTriggerLabel(
  viewFilterImageTypes: Set<string>,
  allTypeKeys: readonly string[],
): string {
  const selectedCount = allTypeKeys.filter((k) => viewFilterImageTypes.has(k))
    .length;
  const total = allTypeKeys.length;
  if (selectedCount === total) return 'Image Type: All';
  if (selectedCount === 0) return 'Image Type';
  const selectedLabels = allTypeKeys
    .filter((k) => viewFilterImageTypes.has(k))
    .map((k) => VIEW_TYPE_TO_LABEL[k] ?? k);
  if (selectedLabels.length > MAX_TYPES_SHOWN) {
    return `Image Type: ${selectedLabels.length} selected`;
  }
  return `Image Type: ${selectedLabels.join(', ')}`;
}

export const ImageTypeDropdown = ({
  viewFilterImageTypes,
  allTypeKeys,
  onToggleType,
  isTypeChecked,
}: ImageTypeDropdownProps) => {
  const imageTypeTriggerLabel = getImageTypeTriggerLabel(
    viewFilterImageTypes,
    allTypeKeys,
  );

  const otherKeys = ['periochart', 'colorImages'];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="vai-w-[220px] vai-min-w-[220px] vai-justify-between vai-text-[#18181B] vai-font-normal vai-text-left vai-border-slate-200"
          variant="outline"
        >
          <span className="vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap vai-min-w-0">
            {imageTypeTriggerLabel}
          </span>
          <ChevronDown className="vai-h-4 vai-w-4 vai-shrink-0 vai-opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="vai-w-[--radix-popper-anchor-width] vai-overflow-y-auto vai-max-h-[50vh] vai-flex vai-flex-col vai-gap-1 vai-p-2"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="vai-text-xs vai-font-semibold vai-text-muted-foreground">
            <Trans>Radiographs</Trans>
          </DropdownMenuLabel>
          {IMAGE_TYPE_MENU_ITEMS.map((item) => {
            const key = 'type' in item ? item.type : item.subtype;
            const label = 'type' in item ? item.label : item.label;
            return (
              <DropdownMenuItem
                key={key}
                onSelect={(e) => e.preventDefault()}
                className="vai-flex vai-items-center vai-gap-2 vai-cursor-pointer"
                onClick={() => onToggleType(key)}
              >
                <Checkbox checked={isTypeChecked(key)} />
                <span>{label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel className="vai-text-xs vai-font-semibold vai-text-muted-foreground">
            <Trans>Other</Trans>
          </DropdownMenuLabel>
          {otherKeys.map((key) => (
            <DropdownMenuItem
              key={key}
              onSelect={(e) => e.preventDefault()}
              className="vai-flex vai-items-center vai-gap-2 vai-cursor-pointer"
              onClick={() => onToggleType(key)}
            >
              <Checkbox checked={isTypeChecked(key)} />
              <span>{VIEW_TYPE_TO_LABEL[key] ?? key}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
